import {
  BodyText,
  Button,
  Callout,
  Separator,
  Skeleton,
  Tooltip,
} from "@/components/ui";
import { useParams } from "@tanstack/react-router";
import { CirclePlus } from "lucide-react";
import { useState } from "react";
import { Fragment } from "react/jsx-runtime";
import type { z } from "zod";
import { SectionContent } from "../components/SectionContent";
import { SectionHeader } from "../components/SectionHeader";
import { HealthInsuranceForm } from "./components/HealthInsuranceForm";
import { HealthInsurancePlan } from "./components/HealthInsurancePlan";
import {
  useBillingHealthInsurance,
  useBillingInsuranceCoverage,
} from "./network/useHealthInsurance";
import type { insuranceFormSchema } from "./schemas/schemas";

export const HealthInsurance = () => {
  const { patientId } = useParams({ strict: false });
  const [adding, setIsAdding] = useState(false);

  type InsuranceFormValues = z.infer<typeof insuranceFormSchema>;

  const { mutate: saveBillingCoverage, isPending: isCreatingBilling } =
    useBillingHealthInsurance(patientId as string, () => setIsAdding(false));

  const activeCoverageQuery = useBillingInsuranceCoverage(patientId, {
    enabled: true,
  });

  const handleSubmit = (coverageDetails: InsuranceFormValues) => {
    saveBillingCoverage({
      carrierId: coverageDetails.payorOrganizationId,
      memberId: coverageDetails.memberId,
      patientRelationship: coverageDetails.subscriberRelationship as
        | "self"
        | "spouse"
        | "child"
        | "other",
      planName: coverageDetails.planCode,
      groupNumber: coverageDetails.groupNumber,
      customerServicePhone: coverageDetails.customerServicePhoneNumber,
    });
  };

  const coverages = activeCoverageQuery.data ?? [];
  const isLoading =
    activeCoverageQuery.isLoading || activeCoverageQuery.isFetching;
  const isError = activeCoverageQuery.isError;

  return (
    <div className="w-full h-full flex flex-col">
      <SectionHeader
        title="Health Insurance"
        actionButtons={
          !isLoading && (!coverages || coverages.length === 0)
            ? [
                <Tooltip tooltipContentText="Add" side="bottom" key="add">
                  <Button
                    variant="icon"
                    className="m-0 p-0"
                    onClick={() => setIsAdding(true)}
                    aria-label="Add health insurance"
                  >
                    <CirclePlus className="stroke-link h-4 w-4" />
                  </Button>
                </Tooltip>,
              ]
            : []
        }
      />
      <SectionContent>
        {isLoading && (
          <div className="flex flex-col gap-2 w-full">
            <Skeleton className="h-36 mt-2" />
            <Separator />
          </div>
        )}
        {isError && (
          <div className="flex py-2 w-full">
            <Callout
              className="w-full"
              variant="danger"
              title="Something went wrong!"
              description="Please refresh and try again."
            />
          </div>
        )}
        {!isLoading && !isError && coverages && (
          <div>
            {coverages?.map((coverage) => (
              <Fragment key={coverage.coverageId}>
                <HealthInsurancePlan coverage={coverage} />
                <Separator />
              </Fragment>
            ))}
          </div>
        )}
        {!isLoading && !isError && !coverages?.length && !adding && (
          <div className="flex relative flex-col gap-2 py-2 w-full">
            <BodyText textColor="tertiary" size="sm">
              No health insurance documented
            </BodyText>
          </div>
        )}
        {adding && !isError && patientId && (
          <div className="flex relative flex-col gap-2 py-2 w-full">
            <HealthInsuranceForm
              isSaving={isCreatingBilling}
              onSubmit={handleSubmit}
              onCancel={() => setIsAdding(false)}
            />
          </div>
        )}
      </SectionContent>
    </div>
  );
};
